import { arraysHaveSameItems, distinct, distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import type { InvestorPortalRole } from "../../../../api/types/investorContactsTypes";
import type {
  MemberChangeAction,
  MemberChangeItem,
  MemberFundPermissions,
  MembersChangeRequestView,
  UpsertMembersChangeRequestRequest,
} from "../../../../api/types/membersChangeRequestTypes";
import type { AccessCategory } from "../../../../api/types/settingsTypes";

// Uniform on purpose: naming a joined request would disclose funds the contact cannot see
export const CHANGE_REQUEST_SUBMITTED_MESSAGE = "Your changes have been submitted for approval";

// The client sees only its own units, so it cannot tell a whole-request cancel from a replace that keeps other funds' units
export const CHANGE_REQUEST_WITHDRAWN_MESSAGE = "Your pending changes have been withdrawn";

export const getWithdrawChangesHeading = (referenceCode: string | undefined): string =>
  referenceCode ? `Withdraw changes from request #${referenceCode}?` : "Withdraw changes from this request?";

// The backend requires both base fields to amend the pending change request and neither to create a new one
export const getBaseChangeRequest = (
  latestChangeRequest: MembersChangeRequestView | undefined
): Pick<UpsertMembersChangeRequestRequest, "baseChangeRequestId" | "baseEtag"> | undefined =>
  latestChangeRequest?.changeRequestId && latestChangeRequest.etag
    ? { baseChangeRequestId: latestChangeRequest.changeRequestId, baseEtag: latestChangeRequest.etag }
    : undefined;

const getPendingRequestComment = (latestChangeRequest: MembersChangeRequestView | undefined): string | undefined =>
  latestChangeRequest?.changeRequestId ? latestChangeRequest.comment : undefined;

// Derived, never stored, so it cannot outlive the request it came from
export const resolveDisplayedComment = (
  typedComment: string | undefined,
  latestChangeRequest: MembersChangeRequestView | undefined
): string | undefined => typedComment ?? getPendingRequestComment(latestChangeRequest);

// Repeats the pending comment when the user left it alone, because the Admin Portal history shows only the newest contribution's comment
export const resolveSubmittedComment = (
  typedComment: string | undefined,
  latestChangeRequest: MembersChangeRequestView | undefined
): string | undefined => {
  const pendingComment = getPendingRequestComment(latestChangeRequest)?.trim();
  const comment = (typedComment ?? pendingComment)?.trim();
  // "" tells the backend to remove the pending comment, so there is nothing to remove without one
  return comment || (pendingComment ? "" : undefined);
};

export type MemberCategoryChangeType = "Added" | "Removed";

export interface MemberCategoryChange {
  name: string;
  changeType: MemberCategoryChangeType;
}

export interface MemberFundChange {
  fundNames: string[];
  role?: InvestorPortalRole;
  roleChanged: boolean;
  categoryChanges: MemberCategoryChange[];
}

export interface MemberChange {
  key: string;
  name: string;
  email: string;
  action: MemberChangeAction;
  fundChanges: MemberFundChange[];
}

export const getChangesCount = (change: MemberChange): number =>
  change.fundChanges.reduce(
    (count, fundChange) => count + fundChange.categoryChanges.length + (fundChange.roleChanged ? 1 : 0),
    0
  );

const toCategoryChanges = (
  categoryExternalIds: string[],
  changeType: MemberCategoryChangeType,
  accessCategories: AccessCategory[]
): MemberCategoryChange[] =>
  categoryExternalIds
    .map((externalId) => accessCategories.find((category) => category.externalId === externalId)?.name)
    .filter(isDefined())
    .map((name) => ({ name, changeType }));

interface RawFundChange {
  fundName: string;
  role?: InvestorPortalRole;
  roleChanged: boolean;
  categoryChanges: MemberCategoryChange[];
}

// Diff per fund: a contact-level union would hide a change on one fund when another fund keeps the same value
const getRawFundChanges = (
  item: MemberChangeItem,
  fundNameById: Map<string, string>,
  accessCategories: AccessCategory[]
): RawFundChange[] => {
  const newByFund = new Map(item.fundPermissions.map((permissions) => [permissions.fundInvestorId, permissions]));
  const oldByFund = new Map(item.oldFundPermissions.map((permissions) => [permissions.fundInvestorId, permissions]));

  const toRawFundChange = (fundInvestorId: string): RawFundChange | undefined => {
    const fundName = fundNameById.get(fundInvestorId);
    if (fundName === undefined) {
      return undefined;
    }

    const newPermissions = newByFund.get(fundInvestorId);
    const oldPermissions = oldByFund.get(fundInvestorId);

    if (item.action === "CreateNew") {
      return {
        fundName,
        role: newPermissions?.role,
        roleChanged: true,
        categoryChanges: toCategoryChanges(newPermissions?.categories ?? [], "Added", accessCategories),
      };
    }

    if (item.action === "Delete") {
      return {
        fundName,
        role: oldPermissions?.role,
        roleChanged: true,
        categoryChanges: toCategoryChanges(oldPermissions?.categories ?? [], "Removed", accessCategories),
      };
    }

    // A fund only removed from is a membership change — out of scope here
    if (!newPermissions) {
      return undefined;
    }

    // An update may attach the member to a fund (no old permissions); render it like a new membership
    if (!oldPermissions) {
      return {
        fundName,
        role: newPermissions.role,
        roleChanged: true,
        categoryChanges: toCategoryChanges(newPermissions.categories, "Added", accessCategories),
      };
    }

    const addedCategoryIds = newPermissions.categories.filter((id) => !oldPermissions.categories.includes(id));
    const removedCategoryIds = oldPermissions.categories.filter((id) => !newPermissions.categories.includes(id));
    const categoryChanges = [
      ...toCategoryChanges(addedCategoryIds, "Added", accessCategories),
      ...toCategoryChanges(removedCategoryIds, "Removed", accessCategories),
    ];
    const roleChanged = newPermissions.role !== oldPermissions.role;

    if (!roleChanged && categoryChanges.length === 0) {
      return undefined;
    }

    return { fundName, role: newPermissions.role, roleChanged, categoryChanges };
  };

  const fundInvestorIds =
    item.action === "Delete"
      ? item.oldFundPermissions.map((permissions) => permissions.fundInvestorId)
      : item.action === "CreateNew"
        ? item.fundPermissions.map((permissions) => permissions.fundInvestorId)
        : distinct([...item.fundPermissions, ...item.oldFundPermissions].map((p) => p.fundInvestorId));

  return fundInvestorIds.map(toRawFundChange).filter(isDefined());
};

const groupFundChanges = (rawFundChanges: RawFundChange[], action: MemberChangeAction): MemberFundChange[] => {
  const groupsBySignature = new Map<string, MemberFundChange>();

  rawFundChanges.forEach((rawChange) => {
    const isRoleShown = action !== "UpdateExisting" || rawChange.roleChanged;
    const signature = JSON.stringify({
      role: isRoleShown ? (rawChange.role ?? null) : null,
      categoryChanges: rawChange.categoryChanges.map((change) => `${change.changeType}:${change.name}`).sort(),
    });

    const group = groupsBySignature.get(signature);
    if (group) {
      group.fundNames.push(rawChange.fundName);
    } else {
      groupsBySignature.set(signature, {
        fundNames: [rawChange.fundName],
        role: rawChange.role,
        roleChanged: rawChange.roleChanged,
        categoryChanges: rawChange.categoryChanges,
      });
    }
  });

  return [...groupsBySignature.values()]
    .map((group) => ({ ...group, fundNames: [...group.fundNames].sort((a, b) => a.localeCompare(b)) }))
    .sort((a, b) => (a.fundNames[0] ?? "").localeCompare(b.fundNames[0] ?? ""));
};

// The access the item leaves behind: it lists only the funds it changes, so untouched ones come from the stored access
const getResultingPermissions = (item: MemberChangeItem): MemberFundPermissions[] => {
  if (item.action === "Delete") {
    return item.oldFundPermissions.map(({ fundInvestorId }) => ({ fundInvestorId, categories: [] }));
  }

  const requestedByFund = new Map(item.fundPermissions.map((permissions) => [permissions.fundInvestorId, permissions]));
  const storedFundIds = new Set(item.oldFundPermissions.map(({ fundInvestorId }) => fundInvestorId));

  return [
    ...item.oldFundPermissions.map((permissions) => requestedByFund.get(permissions.fundInvestorId) ?? permissions),
    ...item.fundPermissions.filter(({ fundInvestorId }) => !storedFundIds.has(fundInvestorId)),
  ];
};

const haveSamePermissions = (permissions: MemberFundPermissions[], others: MemberFundPermissions[]): boolean =>
  permissions.length === others.length &&
  permissions.every((permission) => {
    const other = others.find((candidate) => candidate.fundInvestorId === permission.fundInvestorId);
    return !!other && other.role === permission.role && arraysHaveSameItems(permission.categories, other.categories);
  });

const requestsSameChange = (item: MemberChangeItem, pendingItem: MemberChangeItem): boolean =>
  item.action === pendingItem.action &&
  item.name === pendingItem.name &&
  item.email === pendingItem.email &&
  haveSamePermissions(getResultingPermissions(item), getResultingPermissions(pendingItem));

// The server may fork two operations for one contact, so a cross-action contact match would re-base against the wrong one
const findPendingItem = (item: MemberChangeItem, pendingItems: MemberChangeItem[]): MemberChangeItem | undefined =>
  (item.changeItemId
    ? pendingItems.find((pendingItem) => pendingItem.changeItemId === item.changeItemId)
    : undefined) ??
  (item.contactId
    ? pendingItems.find((pendingItem) => pendingItem.contactId === item.contactId && pendingItem.action === item.action)
    : undefined);

// Dropping a pending change is itself a new change: withdraw a pending addition, or hand back the access a pending removal takes away
const toRevertedItem = (pendingItem: MemberChangeItem): MemberChangeItem =>
  pendingItem.action === "CreateNew"
    ? { ...pendingItem, action: "Delete", fundPermissions: [], oldFundPermissions: pendingItem.fundPermissions }
    : {
        ...pendingItem,
        action: "UpdateExisting",
        name: pendingItem.oldName ?? pendingItem.name,
        email: pendingItem.oldEmail ?? pendingItem.email,
        fundPermissions: pendingItem.oldFundPermissions,
        oldFundPermissions: getResultingPermissions(pendingItem),
      };

// Re-bases the merged request onto the pending one so only the changes made since it was submitted are shown
export const getNewChangesView = (
  mergedChangeRequest: MembersChangeRequestView,
  pendingChangeRequest: MembersChangeRequestView
): MembersChangeRequestView => {
  const pendingItems = pendingChangeRequest.items;
  const matchedPendingItems = new Set<MemberChangeItem>();

  const changedItems = mergedChangeRequest.items
    .map<MemberChangeItem | undefined>((item) => {
      const pendingItem = findPendingItem(item, pendingItems);
      if (!pendingItem) {
        return item;
      }

      matchedPendingItems.add(pendingItem);
      return requestsSameChange(item, pendingItem)
        ? undefined
        : {
            ...item,
            oldName: pendingItem.name ?? pendingItem.oldName,
            oldEmail: pendingItem.email ?? pendingItem.oldEmail,
            oldFundPermissions: getResultingPermissions(pendingItem),
          };
    })
    .filter(isDefined());

  const revertedItems = pendingItems.filter((pendingItem) => !matchedPendingItems.has(pendingItem)).map(toRevertedItem);

  return {
    ...mergedChangeRequest,
    items: [...changedItems, ...revertedItems],
    fundInvestors: distinctBy(
      [...mergedChangeRequest.fundInvestors, ...pendingChangeRequest.fundInvestors],
      (fundInvestor) => fundInvestor.fundInvestorId
    ),
  };
};

export const getMemberChanges = (
  changeRequest: MembersChangeRequestView,
  accessCategories: AccessCategory[]
): MemberChange[] => {
  const fundNameById = new Map(changeRequest.fundInvestors.map((fi) => [fi.fundInvestorId, fi.fundName]));

  return changeRequest.items.map((item, index) => ({
    key: item.changeItemId ?? `item-${index}`,
    name: item.name ?? item.oldName ?? "",
    email: item.email ?? item.oldEmail ?? "",
    action: item.action,
    fundChanges: groupFundChanges(getRawFundChanges(item, fundNameById, accessCategories), item.action),
  }));
};
